import { Component, EventEmitter, Input, OnInit, Output, Optional, ChangeDetectorRef } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { StorageService } from 'src/app/share/services/storage-service/storage.service';
import { StorageKeys } from 'src/app/share/enums/storage.enum';

function checkDuplicatePages(part: string, seenPages: Set<number>): ValidationErrors | null {
  const rangeMatch = /^(\d+)-(\d+)$/.exec(part);
  if (rangeMatch) {
    const start = Number(rangeMatch[1]);
    const end = Number(rangeMatch[2]);
    for (let i = start; i <= end; i++) {
      if (seenPages.has(i)) return { duplicatePages: true };
      seenPages.add(i);
    }
  } else {
    const singleMatch = /^(\d+)$/.exec(part);
    if (singleMatch) {
      const pageNum = Number(singleMatch[1]);
      if (seenPages.has(pageNum)) return { duplicatePages: true };
      seenPages.add(pageNum);
    }
  }
  return null;
}

function createPageRangeValidator(maxPages: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = (control.value || '').trim();
    if (!value) return null;
    const parts = value.split(',').map(s => s.trim());
    const seenPages = new Set<number>();   
    for (const part of parts) {
      const error = validatePagePart(part, maxPages);
      if (error) return error;
      const dupError = checkDuplicatePages(part, seenPages);
      if (dupError) return dupError;
    }
    return null;
  };
}

function createPageCountValidator(maxPages: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const val = Number(control.value);
    if (val === null || val === undefined || control.value === '') return null;
    if (val < 1) return { min: true };
    if (maxPages && val > maxPages) return { pageCountExceeds: true };
    return null;
  };
}

function createFileSizeValidator(maxBytes: number, unit: 'KB' | 'MB' | 'GB'): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const val = Number(control.value);
    if (val === null || val === undefined || control.value === '') return null;
    if (val <= 0) return { min: true };
    if (maxBytes) {
      let divisor = 1024;
      if (unit === 'GB') divisor = 1024 ** 3;
      else if (unit === 'MB') divisor = 1024 ** 2;
      if (val > maxBytes / divisor) return { sizeExceeds: true };
    }
    return null;
  };
}

@Component({
  selector: 'app-pdf-download-options',
  templateUrl: './pdf-download-options.html',
  styleUrl: './pdf-download-options.scss',
  standalone: false
})
export class PdfDownloadOptions implements OnInit {
  @Input() fileName: string = '';
  @Input() totalPages: number = 0;
  @Input() fileSizeBytes: number = 0;
  @Input() allowedDownloadOptions: string[] | null = null;
  @Output() downloadSelected = new EventEmitter<any>();
  @Output() closePopup = new EventEmitter<void>();

  downloadForm: FormGroup;
  private readonly prefKeyMap: Record<string, string> = {
    RANGE:      'pageRange',
    PAGE_COUNT: 'pageCount',
    FILE_SIZE:  'fileSize',
    COMPRESS:   'compression',
  };

  get fileSizeUnit(): 'KB' | 'MB' | 'GB' {
    if (this.fileSizeBytes >= 1024 ** 3) return 'GB';
    if (this.fileSizeBytes >= 1024 ** 2) return 'MB';
    return 'KB';
  }

  get maxFileSizeDisplay(): string {
    if (!this.fileSizeBytes) return '';
    let divisor = 1024;
    if (this.fileSizeUnit === 'GB') divisor = 1024 ** 3;
    else if (this.fileSizeUnit === 'MB') divisor = 1024 ** 2;
    return `${(this.fileSizeBytes / divisor).toFixed(2)} ${this.fileSizeUnit}`;
  }

  private readonly allOptions = [
    { label: 'Full Document',       value: 'FULL',       description: 'Download the entire PDF as a single file.' },
    { label: 'Page Range',          value: 'RANGE',      description: 'Download specific pages or page ranges.' },
    { label: 'Split by Page Count', value: 'PAGE_COUNT', description: 'Split into files with a fixed number of pages.' },
    { label: 'Split by File Size',  value: 'FILE_SIZE',  description: 'Split into files up to a maximum size.' },
    { label: 'Compress PDF',        value: 'COMPRESS',   description: 'Reduce file size using server-side compression.' },
  ];

  readonly compressionLevels = [
    { label: 'Extreme Compression', value: 'Extreme Compression',     hint: 'Smallest file size, may reduce quality.' },
    { label: 'Recommended',         value: 'Recommended Compression', hint: 'Best balance of size and quality.' },
    { label: 'Less Compression',    value: 'Less Compression',        hint: 'Minimal compression, preserves quality.' },
  ];

  get hasPageCount(): boolean {
    return !!this.totalPages && this.totalPages > 0;
  }

  get hasFileSize(): boolean {
    return !!this.fileSizeBytes && this.fileSizeBytes > 0;
  }

  isAllowedByPref(optionValue: string): boolean {
    if (optionValue === 'FULL') return true;
    if (!this.allowedDownloadOptions) return true;           // no restriction — show all
    const prefKey = this.prefKeyMap[optionValue];
    return !!prefKey && this.allowedDownloadOptions.includes(prefKey);
  }

  get options() {
    return this.allOptions.filter(opt => {
      if (!this.isAllowedByPref(opt.value)) return false;
      if (opt.value === 'FULL') return true;
      if (opt.value === 'RANGE' || opt.value === 'PAGE_COUNT') return this.hasPageCount;
      if (opt.value === 'FILE_SIZE') return this.hasFileSize;
      return true;
    });
  }

  get hasExtraOptions(): boolean {
    return this.options.some(o => o.value !== 'FULL');
  }

  get actionButtonLabel(): string {
    return this.downloadForm.get('downloadType')?.value === 'COMPRESS' ? 'Compress' : 'Download';
  }

  constructor(
    @Optional() public readonly activeModal: NgbActiveModal,
    private readonly cdr: ChangeDetectorRef,
    private readonly storageService: StorageService
  ) {
    this.downloadForm = new FormGroup({
      downloadType:     new FormControl('FULL', Validators.required),
      pageRange:        new FormControl(''),
      pageCount:        new FormControl(''),
      fileSize:         new FormControl(''),
      compressionLevel: new FormControl('Recommended Compression'),
    });
  }

  ngOnInit() {
    if (this.allowedDownloadOptions === null) {
      const prefs = this.storageService.getDataFromLocalStorage(StorageKeys.PREFERENCES);
      const downloadOptions = prefs?.documentVault?.downloadOptions;
      if (downloadOptions !== undefined && downloadOptions !== null) {
      if (downloadOptions?.enabled && Array.isArray(downloadOptions?.values)) {
        this.allowedDownloadOptions = downloadOptions.values;
        } else {
          this.allowedDownloadOptions = [];
        }
      } else {
        this.allowedDownloadOptions = null;
      }
    }
    const current = this.downloadForm.get('downloadType')?.value;
    if (!this.options.some(o => o.value === current)) {
      this.downloadForm.get('downloadType')?.setValue('FULL');
    }

    this.downloadForm.get('downloadType')?.valueChanges.subscribe(type => {
      this.clearValidators();
      if (type === 'RANGE') {
        this.downloadForm.get('pageRange')?.setValidators([
          Validators.required,
          createPageRangeValidator(this.totalPages),
        ]);
      } else if (type === 'PAGE_COUNT') {
        this.downloadForm.get('pageCount')?.setValidators([
          Validators.required,
          createPageCountValidator(this.totalPages),
        ]);
      } else if (type === 'FILE_SIZE') {
        this.downloadForm.get('fileSize')?.setValidators([
          Validators.required,
          createFileSizeValidator(this.fileSizeBytes, this.fileSizeUnit),
        ]);
      } else if (type === 'COMPRESS') {
        this.downloadForm.get('compressionLevel')?.setValidators([Validators.required]);
        if (!this.downloadForm.get('compressionLevel')?.value) {
          this.downloadForm.get('compressionLevel')?.setValue('Recommended Compression');
        }
      }
      ['pageRange', 'pageCount', 'fileSize', 'compressionLevel'].forEach(f =>
        this.downloadForm.get(f)?.updateValueAndValidity({ emitEvent: false })
      );
      this.downloadForm.updateValueAndValidity();
      this.cdr.detectChanges();
    });

    this.downloadForm.statusChanges.subscribe(() => this.cdr.detectChanges());
  }

  clearValidators() {
    ['pageRange', 'pageCount', 'fileSize'].forEach(field => {
      const control = this.downloadForm.get(field);
      control?.clearValidators();
      control?.setValue('');
      control?.setErrors(null);
    });
    const compLevel = this.downloadForm.get('compressionLevel');
    compLevel?.clearValidators();
    compLevel?.setErrors(null);
  }

  close() {
    if (this.activeModal) this.activeModal.close();
    this.closePopup.emit();
  }

  download() {
    if (this.downloadForm.invalid) return;
    const form = this.downloadForm.value;
    let payload: any = {};
    if (form.downloadType === 'FILE_SIZE') {
      payload = { splitBySize: form.fileSize, unit: this.fileSizeUnit };
    } else if (form.downloadType === 'RANGE') {
      payload = { pageRange: (form.pageRange as string).replaceAll(/\s/g, '') };
    } else if (form.downloadType === 'PAGE_COUNT') {
      payload = { splitByPage: form.pageCount };
    } else if (form.downloadType === 'COMPRESS') {
      payload = { isCompress: true, compressionLevel: form.compressionLevel };
    }
    if (this.activeModal) this.activeModal.close(payload);
    this.downloadSelected.emit(payload);
  }
}



    function validatePagePart(part: string, maxPages: number): ValidationErrors | null {
      const rangeMatch = /^(\d+)-(\d+)$/.exec(part);
      if (rangeMatch) {
      return validatePageRange(rangeMatch, maxPages);
    } 
      const singleMatch = /^(\d+)$/.exec(part);
      if (singleMatch) {
      return validateSinglePage(singleMatch, maxPages);
    }
      return { rangeFormat: true };
  }

    function validatePageRange(rangeMatch: RegExpExecArray, maxPages: number): ValidationErrors | null {
      const start = Number(rangeMatch[1]);
      const end = Number(rangeMatch[2]);
      if (start < 1) return { rangeMin: true };
      if (start > end) return { rangeOrder: true };
      if (maxPages && end > maxPages) return { rangeExceeds: true };
      return null;
    }

    function validateSinglePage(singleMatch: RegExpExecArray, maxPages: number): ValidationErrors | null {
      const pageNum = Number(singleMatch[1]);
      if (pageNum < 1) return { rangeMin: true };
      if (maxPages && pageNum > maxPages) return { rangeExceeds: true };
      return null;
    }